import React, { useState, useEffect } from 'react';
import {
  Mail,
  MessageSquare,
  Clock,
  Send,
  CheckCircle2,
  HelpCircle,
  FileText,
  MessageCircle,
  ExternalLink,
  ShieldCheck,
  RefreshCw
} from 'lucide-react';

import { CMSContent } from '../types';
import {
  ContactCenterSettings,
  DEFAULT_CONTACT_CENTER_SETTINGS,
  getLocalContactCenterSettings,
  subscribeToContactCenterSettings,
  submitContactMessage
} from '../lib/contactCenterService';

interface ContactPageProps {
  darkMode: boolean;
  onOpenAI?: () => void;
  cms?: CMSContent;
}

export const ContactPage: React.FC<ContactPageProps> = ({ darkMode, cms }) => {
  const [settings, setSettings] = useState<ContactCenterSettings>(getLocalContactCenterSettings());

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: 'General Support',
    message: '',
    website: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const unsub = subscribeToContactCenterSettings((newSettings) => {
      setSettings(newSettings);
    });
    return () => unsub();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.website) {
      setSubmitted(true); // Silently accept to fool the bot
      return;
    }
    if (!formData.name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.message.trim()) {
      return;
    }
    setLoading(true);

    try {
      const ticket = await submitContactMessage({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        category: formData.category,
        message: formData.message
      });

      setTicketId(ticket.id);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting contact message:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setTicketId('');
    setFormData({
      name: '',
      email: '',
      subject: '',
      category: 'General Support',
      message: '',
      website: '',
    });
  };

  return (
    <div className="relative z-10 py-16 overflow-hidden min-h-screen flex flex-col justify-center">
      {/* Subtle Futuristic Background (Right/Back) */}
      <div className="absolute top-0 right-0 w-full lg:w-[800px] h-[800px] bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-900/10 via-[#0f172a]/0 to-transparent pointer-events-none opacity-40 blur-3xl"></div>
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full border-[1px] border-blue-500/5 shadow-[0_0_60px_inset_rgba(59,130,246,0.03)] opacity-20 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-stretch">
          
          {/* Left Side: Information */}
          <div className="flex flex-col justify-between h-full lg:pr-8 space-y-8 lg:space-y-0">
            
            {/* Header Hero Section */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800/50 border border-slate-700 text-slate-300 text-[10px] font-semibold uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                CONTACT & SUPPORT
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1]">
                How can we <br className="hidden sm:block" />
                <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">help you</span> today?
              </h1>

              <p className="text-base text-slate-400 max-w-md leading-relaxed">
                Reach out to us through any of the channels below or send us a message.
              </p>
            </div>

            {/* 4 Cards Grid (2x2) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              
              {/* Card 1: Email */}
              <div className="p-6 rounded-2xl bg-[#0b1120]/80 border border-blue-500/20 md:hover:border-cyan-400/80 md:hover:shadow-[0_0_20px_rgba(6,182,212,0.4),inset_0_0_12px_rgba(6,182,212,0.15)] md:hover:-translate-y-px transition-all duration-[400ms] md:hover:duration-[250ms] flex flex-col justify-center gap-3 group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-2xl bg-[#0070f3] flex items-center justify-center text-white shadow-[0_0_25px_rgba(0,112,243,0.5)] md:group-hover:shadow-[0_0_30px_rgba(6,182,212,0.7)] transition-all duration-[400ms] md:group-hover:duration-[250ms]">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">{settings.email.title || 'Email Support'}</h3>
                    <a href={settings.email.actionUrl || "mailto:support@rheditography.net"} className="text-[13px] text-slate-400 hover:text-white transition-colors mt-1 block">
                      {settings.email.value || 'support@rheditography.net'}
                    </a>
                  </div>
                </div>
              </div>

              {/* Card 2: Telegram */}
              <div className="p-6 rounded-2xl bg-[#0b1120]/80 border border-indigo-500/20 md:hover:border-blue-400/80 md:hover:shadow-[0_0_20px_rgba(59,130,246,0.4),inset_0_0_12px_rgba(59,130,246,0.15)] md:hover:-translate-y-px transition-all duration-[400ms] md:hover:duration-[250ms] flex flex-col justify-center gap-3 group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-2xl bg-[#2563eb] flex items-center justify-center text-white shadow-[0_0_25px_rgba(37,99,235,0.5)] md:group-hover:shadow-[0_0_30px_rgba(59,130,246,0.7)] transition-all duration-[400ms] md:group-hover:duration-[250ms]">
                    <Send className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">{settings.telegram.title || 'Telegram Support'}</h3>
                    <a href={settings.telegram.actionUrl || "https://t.me/rheditography"} target="_blank" rel="noreferrer" className="text-[13px] text-slate-400 hover:text-white transition-colors mt-1 block">
                      {settings.telegram.value || '@rheditography'}
                    </a>
                  </div>
                </div>
              </div>

              {/* Card 3: WhatsApp */}
              <div className="p-6 rounded-2xl bg-[#0b1120]/80 border border-emerald-500/20 md:hover:border-emerald-400/80 md:hover:shadow-[0_0_20px_rgba(16,185,129,0.4),inset_0_0_12px_rgba(16,185,129,0.15)] md:hover:-translate-y-px transition-all duration-[400ms] md:hover:duration-[250ms] flex flex-col justify-center gap-3 group">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-2xl bg-[#10b981] flex items-center justify-center text-white shadow-[0_0_25px_rgba(16,185,129,0.4)] md:group-hover:shadow-[0_0_30px_rgba(16,185,129,0.7)] transition-all duration-[400ms] md:group-hover:duration-[250ms]">
                    <MessageCircle className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white">{settings.whatsapp.title || 'WhatsApp Support'}</h3>
                    <a href={settings.whatsapp.actionUrl || "https://wa.me/14076588573"} target="_blank" rel="noreferrer" className="text-[13px] text-slate-400 hover:text-white transition-colors mt-1 block">
                      {settings.whatsapp.value || '+1 (407) 658-8573'}
                    </a>
                  </div>
                </div>
              </div>

              {/* Card 4: Support Block */}
              {settings.supportBlock.enabled && (
                <div className="p-6 rounded-2xl bg-[#0b1120]/80 border border-purple-500/20 md:hover:border-purple-400/80 md:hover:shadow-[0_0_20px_rgba(168,85,247,0.4),inset_0_0_12px_rgba(168,85,247,0.15)] md:hover:-translate-y-px transition-all duration-[400ms] md:hover:duration-[250ms] flex flex-col justify-center gap-3 group">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 shrink-0 rounded-2xl bg-[#8b5cf6] flex items-center justify-center text-white shadow-[0_0_25px_rgba(139,92,246,0.5)] md:group-hover:shadow-[0_0_30px_rgba(168,85,247,0.7)] transition-all duration-[400ms] md:group-hover:duration-[250ms]">
                      <Clock className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-white">{settings.supportBlock.title || 'Support Hours'}</h3>
                      <div className="text-[13px] text-slate-400 mt-1 leading-snug">
                        <p>{settings.supportBlock.subtitle || 'Mon - Sat (GMT)'}</p>
                        <p>{settings.supportBlock.value || '24/7 Downloads'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Reassurance Card */}
            <div className="p-5 rounded-2xl bg-[#0b1120]/60 border border-slate-800/80 flex items-start gap-4">
               <div className="mt-0.5 shrink-0">
                  <ShieldCheck className="w-7 h-7 text-blue-500" strokeWidth={1.5} />
               </div>
               <div>
                  <h3 className="text-sm font-bold text-white">We're here to help you</h3>
                  <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">Your satisfaction is our priority. Our team will respond as quickly as possible.</p>
               </div>
            </div>

          </div>

          {/* Right Side: Contact Form Container */}
          <div className="lg:pl-4">
            <div className="relative p-[1px] rounded-[24px] bg-gradient-to-br from-[#0070f3] via-[#8b5cf6] to-[#06b6d4] shadow-[0_0_30px_rgba(139,92,246,0.2)] md:hover:shadow-[0_0_30px_rgba(6,182,212,0.4),0_0_30px_rgba(139,92,246,0.4)] md:hover:-translate-y-px transition-all duration-[400ms] md:hover:duration-[250ms] group">
              <div className="p-6 sm:p-8 rounded-[23px] bg-[#0b1120] h-full w-full relative group-hover:shadow-[inset_0_0_20px_rgba(139,92,246,0.15)] transition-shadow duration-[400ms] md:group-hover:duration-[250ms]">
                {/* Top subtle blue/purple line - internal accent */}
                <div className="absolute top-0 left-8 w-20 h-[3px] bg-gradient-to-r from-blue-500 to-purple-500 rounded-b-full md:group-hover:from-cyan-400 md:group-hover:to-purple-400 md:group-hover:shadow-[0_0_10px_rgba(6,182,212,0.8)] transition-all duration-[400ms] md:group-hover:duration-[250ms]"></div>

                <div className="flex items-center gap-4 mb-8 mt-2">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400">
                    <MessageSquare className="w-5 h-5" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">Send us a message</h2>
                </div>

              {submitted ? (
                <div className="py-12 text-center space-y-4 animate-in fade-in duration-300">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto text-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.2)]">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Message Sent</h3>
                  <div className="space-y-2">
                    <p className="text-sm text-slate-400 max-w-sm mx-auto leading-relaxed">
                      Thank you for reaching out. We have created ticket <span className="font-mono text-blue-400">#{ticketId}</span> and sent a confirmation email to <span className="text-slate-300">{formData.email}</span>.
                    </p>
                  </div>
                  <button
                    onClick={resetForm}
                    className="px-6 py-3 rounded-xl bg-slate-800 text-white font-semibold text-xs border border-slate-700 hover:bg-slate-700 hover:border-slate-600 transition-all mt-6"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Honeypot */}
                  <div className="hidden" aria-hidden="true">
                    <label>Website</label>
                    <input 
                      type="text" 
                      name="website" 
                      tabIndex={-1} 
                      autoComplete="off" 
                      value={formData.website}
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-[13px] font-medium text-slate-300">Full Name</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g. Alex Rivera"
                        className="w-full px-4 py-3.5 rounded-xl bg-[#0f172a]/80 border border-slate-700/80 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-[13px] font-medium text-slate-300">Email Address</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="you@example.com"
                        className="w-full px-4 py-3.5 rounded-xl bg-[#0f172a]/80 border border-slate-700/80 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-[13px] font-medium text-slate-300">Inquiry Category</label>
                      <div className="relative">
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          className="w-full px-4 py-3.5 rounded-xl bg-[#0f172a]/80 border border-slate-700/80 text-sm text-white appearance-none focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                        >
                          <option value="General Support">General Support</option>
                          <option value="Product & Download Support">Product & Download Support</option>
                          <option value="Custom Design Request">Custom Design Request</option>
                          <option value="Order & Payment Issue">Order & Payment Issue</option>
                          <option value="Billing & Refund">Billing & Refund</option>
                          <option value="Commercial Licensing">Commercial Licensing</option>
                          <option value="Other Inquiry">Other Inquiry</option>
                        </select>
                        <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-slate-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[13px] font-medium text-slate-300">Subject</label>
                      <input
                        type="text"
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        placeholder="Short subject"
                        className="w-full px-4 py-3.5 rounded-xl bg-[#0f172a]/80 border border-slate-700/80 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[13px] font-medium text-slate-300">Message</label>
                    <textarea
                      rows={5}
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="How can we help you?"
                      className="w-full px-4 py-3.5 rounded-xl bg-[#0f172a]/80 border border-slate-700/80 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 mt-2 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-500 text-white font-semibold text-[15px] shadow-[0_0_20px_rgba(59,130,246,0.25)] hover:shadow-[0_0_25px_rgba(59,130,246,0.35)] transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed border border-white/5"
                  >
                    {loading ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin opacity-80" />
                        <span>Sending Message...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 opacity-90" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                  
                  <div className="pt-3 flex items-center justify-center gap-1.5 text-slate-500">
                    <ShieldCheck className="w-3.5 h-3.5 opacity-80" />
                    <span className="text-[11px]">Your information is safe with us. We never share your data.</span>
                  </div>
                </form>
              )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
